// src/pages/healthcare/appointments/components/SlotActionPopover.tsx
import React from "react";
import { createPortal } from "react-dom";
import { Alert, Button, Popover, Space, Typography } from "antd";
import { CalendarOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { formatLocalDate, formatLocalTime } from "../../../../utils/userPrefs/DateTimeUtils";
import type { UserUuid } from "../../../../types/identity";

export interface SlotSelection {
	start: Date;
	end: Date;
	// null in the all-staff week, which has no column.
	practitionerId: UserUuid | null;
	// RBC's selection bounds are PAGE coordinates, so the anchor is portalled to the body to match.
	anchor: { left: number; top: number };
	spansDays: boolean;
}

interface SlotActionPopoverProps {
	selection: SlotSelection;
	canEditSchedule: boolean;
	onNewAppointment: () => void;
	onBlockOutTime: () => void;
	onAddAvailability: () => void;
	onClose: () => void;
}

const ACTION_STYLE: React.CSSProperties = { textAlign: "left", paddingLeft: 8 };

const SlotActionPopover: React.FC<SlotActionPopoverProps> = ({ selection, canEditSchedule, onNewAppointment, onBlockOutTime, onAddAvailability, onClose }) => {
	const showScheduleActions = canEditSchedule && !selection.spansDays;

	const note = selection.spansDays ? "Schedule changes are made one day at a time — drag inside a single day." : null;

	const content = (
		<div data-testid="appointments-slot-actions" style={{ minWidth: 240 }}>
			<Typography.Text strong style={{ fontSize: 12 }}>
				{formatLocalDate(selection.start)} · {formatLocalTime(selection.start)} – {formatLocalTime(selection.end)}
			</Typography.Text>

			<Space direction="vertical" size={2} style={{ display: "flex", marginTop: 8 }}>
				<Button block type="text" icon={<PlusOutlined />} style={ACTION_STYLE} onClick={onNewAppointment} data-testid="appointments-slot-new-appointment">
					New appointment
				</Button>
				{showScheduleActions && (
					<>
						<Button block type="text" icon={<StopOutlined />} style={ACTION_STYLE} onClick={onBlockOutTime} data-testid="appointments-block-out-time">
							Block out time
						</Button>
						<Button block type="text" icon={<CalendarOutlined />} style={ACTION_STYLE} onClick={onAddAvailability} data-testid="appointments-add-availability">
							Add availability
						</Button>
					</>
				)}
			</Space>

			{note && <Alert type="info" showIcon message={note} style={{ marginTop: 8 }} data-testid="appointments-slot-actions-note" />}
		</div>
	);

	return createPortal(
		<>
			<div style={{ position: "fixed", inset: 0, zIndex: 1030 }} onClick={onClose} data-testid="appointments-slot-actions-backdrop" />
			<div style={{ position: "absolute", left: selection.anchor.left, top: selection.anchor.top, width: 0, height: 0, zIndex: 1031 }}>
				<Popover open content={content} trigger={[]} placement="rightTop" arrow={false}>
					<span />
				</Popover>
			</div>
		</>,
		document.body
	);
};

export default SlotActionPopover;
